/**
 * SIM-SZENE LEIPZIG – Szenario-Auswahl
 * Gewichtete Zufallsauswahl basierend auf Spielzustand.
 * Phasen & Pfade: Phase 1 → (Pfad-Frage) → Phase 2 (pro Pfad) → (Pfad-Frage) → Phase 3.
 */
import { scenarios, balancing, getScenarioById } from '../data/loader.js';

const REPLAY_COOLDOWN = 10;

/**
 * Prüft, ob die Ressourcen-Bedingungen eines Pfad-Triggers erfüllt sind.
 * when: { geld: { min?: number, max?: number }, nerven: {...}, credibility: {...} }
 */
function matchesPathTriggerWhen(when, state) {
  if (!when || typeof when !== 'object') return true;
  const { geld, nerven, credibility } = state;
  const resources = { geld, nerven, credibility };
  for (const [key, bounds] of Object.entries(when)) {
    const value = resources[key];
    if (value === undefined) continue;
    if (bounds.min !== undefined && value < bounds.min) return false;
    if (bounds.max !== undefined && value > bounds.max) return false;
  }
  return true;
}

/**
 * Gibt den Gewichtungs-Boost für ein Szenario zurück, wenn ein Pfad-Trigger greift.
 * @param {object} scenario - Szenario
 * @param {object} state - Spielzustand (flags, geld, nerven, credibility)
 * @returns {number} Multiplikator (1 = kein Boost)
 */
function getPathTriggerBoost(scenario, state) {
  const triggers = balancing.pathTriggers ?? [];
  for (const trigger of triggers) {
    if (trigger.scenarioId !== scenario.id) continue;
    if (trigger.requires && trigger.requires.length > 0) {
      const hasAll = trigger.requires.every(flag => state.flags[flag]);
      if (!hasAll) continue;
    }
    if (!matchesPathTriggerWhen(trigger.when, state)) continue;
    return Math.max(1, Number(trigger.weightBoost) || 2);
  }
  return 1;
}

/**
 * Prüft, ob ein Szenario im aktuellen Phasen-Pool liegt.
 */
function inPhasePool(scenario, phase, currentPathId) {
  const phases = scenario.phases;
  if (!phases || !Array.isArray(phases)) {
    return phase === 1;
  }
  if (!phases.includes(phase)) return false;
  if (phase === 1) return true;
  if (phase === 2 || phase === 3) {
    const pathMatch = scenario.path === currentPathId ||
      (Array.isArray(scenario.paths) && scenario.paths.includes(currentPathId));
    return pathMatch;
  }
  return true;
}

/**
 * Liefert ein Pfadentscheidungs-Szenario für den Phasenübergang (oder null).
 */
function tryPathDecisionScenario(state) {
  const phase = state.phase ?? 1;
  const decisionsInPhase = state.decisionsInPhase ?? 0;
  const currentPathId = state.currentPathId;
  const playedScenarios = state.playedScenarios ?? [];
  const pathConfig = balancing.pathDecisions;
  const after = balancing.pathDecisionAfterDecisions ?? { min: 4, max: 8 };
  const chance = balancing.pathDecisionChance ?? 0.4;

  if (phase >= 3) return null;
  if (decisionsInPhase < after.min || decisionsInPhase > after.max) return null;
  if (Math.random() >= chance) return null;
  if (!pathConfig) return null;

  let candidateIds = [];
  if (phase === 1 && pathConfig['1_to_2']) {
    candidateIds = Array.isArray(pathConfig['1_to_2']) ? pathConfig['1_to_2'] : [];
  } else if (phase === 2 && currentPathId && pathConfig['2_to_3'] && pathConfig['2_to_3'][currentPathId]) {
    candidateIds = Array.isArray(pathConfig['2_to_3'][currentPathId]) ? pathConfig['2_to_3'][currentPathId] : [];
  }
  if (candidateIds.length === 0) return null;

  const recent = playedScenarios.slice(-REPLAY_COOLDOWN);
  const notRecent = candidateIds.filter(id => !recent.includes(id));
  const ids = notRecent.length > 0 ? notRecent : candidateIds;
  const id = ids[Math.floor(Math.random() * ids.length)];
  const scenario = getScenarioById(id);
  return scenario ?? null;
}

/**
 * Wählt das nächste Szenario basierend auf dem aktuellen State
 *
 * 0. Optional: Pfadentscheidungs-Frage (wenn decisionsInPhase 4–8 und Zufall)
 * 1. Pool nach Phase/Pfad filtern
 * 2. Cooldown + requires
 * 3. Gewichtung (Ressourcen, pathTriggers)
 * 4. Zufallsauswahl
 */
export function selectNextScenario(state) {
  const { round, playedScenarios, flags, geld, nerven, credibility } = state;
  const phase = state.phase ?? 1;
  const currentPathId = state.currentPathId ?? null;

  const pathDecision = tryPathDecisionScenario(state);
  if (pathDecision) {
    return pathDecision;
  }

  // 1. Pool: nach Phase und Pfad filtern
  const phaseFiltered = scenarios.filter(scenario => inPhasePool(scenario, phase, currentPathId));

  const available = (phaseFiltered.length > 0 ? phaseFiltered : scenarios).filter(scenario => {
    const lastPlayedIndex = playedScenarios.lastIndexOf(scenario.id);
    if (lastPlayedIndex !== -1) {
      const roundsSincePlayed = playedScenarios.length - lastPlayedIndex;
      if (roundsSincePlayed < REPLAY_COOLDOWN) return false;
    }
    if (scenario.requires && scenario.requires.length > 0) {
      const hasAllFlags = scenario.requires.every(flag => flags[flag]);
      if (!hasAllFlags) return false;
    }
    return true;
  });

  const pool = available.length > 0 ? available : scenarios;
  
  // 2. Gewichtung basierend auf Ressourcen + Pfad-Trigger (Pfadentscheidungen + Werte)
  const weighted = pool.map(scenario => {
    let weight = scenario.weight ?? 5; // Default-Gewicht
    
    // Pfadlogik: Wenn Pfad-Trigger für dieses Szenario greift (Flags + when), Gewicht erhöhen
    const pathBoost = getPathTriggerBoost(scenario, state);
    weight *= pathBoost;
    
    // Kategorien die zu niedrigen Ressourcen passen, werden verstärkt
    // (Reigns-Prinzip: Probleme werden angezogen)
    
    // Finanzielle Probleme?
    if (geld < 30) {
      if (['A', 'F', 'M'].includes(scenario.category)) {
        weight *= 1.5;
      }
    }
    
    // Stress-Probleme?
    if (nerven < 30) {
      if (['C', 'D', 'P', 'R'].includes(scenario.category)) {
        weight *= 1.5;
      }
    }
    
    // Reputations-Probleme?
    if (credibility < 30) {
      if (['E', 'G', 'H'].includes(scenario.category)) {
        weight *= 1.5;
      }
    }
    
    // Early Game: Leichtere Kategorien bevorzugen
    if (round < 10) {
      if (['I', 'M'].includes(scenario.category)) {
        weight *= 0.3; // Endgame/Bittere Pillen seltener am Anfang
      }
    }
    
    // Late Game: Schwere Kategorien verstärken
    if (round > 100) {
      if (['I', 'M', 'C'].includes(scenario.category)) {
        weight *= 1.5;
      }
    }
    
    return { scenario, weight };
  });
  
  // 3. Gewichtete Zufallsauswahl
  return weightedRandom(weighted);
}

/**
 * Gewichtete Zufallsauswahl
 * @param {Array<{scenario, weight}>} items - Gewichtete Items
 * @returns {object} Zufällig ausgewähltes Szenario
 */
function weightedRandom(items) {
  const totalWeight = items.reduce((sum, item) => sum + item.weight, 0);
  let random = Math.random() * totalWeight;
  
  for (const item of items) {
    random -= item.weight;
    if (random <= 0) {
      return item.scenario;
    }
  }
  
  // Fallback (sollte nicht passieren)
  return items[0]?.scenario ?? scenarios[0];
}

/**
 * Holt alle verfügbaren Kategorien für Debug-Zwecke
 */
export function getAvailableCategories() {
  const categories = new Set(scenarios.map(s => s.category));
  return Array.from(categories).map(id => ({
    id,
    label: balancing.categories[id]?.label ?? id,
    color: balancing.categories[id]?.color ?? '#666'
  }));
}
